/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { css, cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useFireAnalyticsEvent } from '../../common/analytics/use-fire-analytics';
import { useInsightsAnalyticsState } from '../../common/analytics/use-insights-analytics-state';

import { DebugGroupPanel } from './debug/debug-group-panel';
import { DebugSummaryBar } from './debug/debug-summary-bar';
import { DebugThinkingPanel } from './debug/debug-thinking-panel';
import { DebugToggle } from './debug/debug-toggle';
import { AnimatedFeedSection } from './feed-section-animation';
import { InsightsFeedAiDisclaimer } from './insights-feed-ai-disclaimer';
import { InsightsFeedError } from './insights-feed-error';
import { InsightsFeedGroup } from './insights-feed-group';
import { InsightsFeedHeader } from './insights-feed-header';
import { InsightsFeedLoadingShimmer } from './insights-feed-loading-shimmer';
import type { InsightsFeedProps } from './types';
import { useInsightsFeed } from './useInsightsFeed';

/* Debug toggle: hidden by default, revealed on hover or when debug is active */
const debugStyles = cssMap({
	headerHoverContainer: {
		position: 'relative',
	},
	debugToggleWrapper: {
		position: 'absolute',
		insetInlineEnd: '0',
		insetBlockStart: token('space.100'),
		opacity: 0,
		transition: 'opacity 150ms ease-in-out',
	},
	debugToggleWrapperActive: {
		position: 'absolute',
		insetInlineEnd: '0',
		insetBlockStart: token('space.100'),
		opacity: 1,
		transition: 'opacity 150ms ease-in-out',
	},
});

const scrollContainerBaseStyles = css({
	flexGrow: 1,
	overflow: 'auto',
	backgroundColor: token('elevation.surface'),
	borderBlockEnd: 'none',
});

const scrollContainerPaddingStyles = cssMap({
	default: {
		paddingBlockStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
	},
	fullScreen: {
		paddingBlockStart: token('space.100'),
		paddingInlineEnd: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
	},
});

const styles = cssMap({
	mainContent: {
		flexGrow: 1,
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	},
	centeredContainer: {
		width: '100%',
		'@media (min-width: 64rem)': {
			maxWidth: 'min(800px, 50vw)',
			marginInline: 'auto',
		},
	},
	listContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 0,
	},
});

/**
 * InsightsFeed — Top-level component that composes InsightsFeedHeader
 * and multiple InsightsFeedGroup components into the full weekly insights list view.
 *
 * Fetches live data from the Rovo Insights API (`/rovo/v1/insights/fetch`).
 * Shows a loading shimmer while fetching and an error screen with retry on failure.
 */
const InsightsFeedBase = ({
	chatRefreshHeader,
	assistanceServiceParams,
	onInsightReady,
	invokedFrom,
	isFullScreen = false,
	connectAppsAction,
}: InsightsFeedProps): JSX.Element => {
	const { fireAnalyticsEvent } = useFireAnalyticsEvent();
	const { onFeedContentDisplayed, onInsightCardClicked, onUrlClicked, onRetryClicked } =
		useInsightsAnalyticsState({ invokedFrom });
	const hasScreenFired = useRef(false);
	const hasContentFired = useRef(false);
	const isDebugGateEnabled = fg('rovo-growth-insights-debug-enabled');
	const [debugEnabled, setDebugEnabled] = useState(false);
	const [headerHovered, setHeaderHovered] = useState(false);
	const handleHeaderMouseEnter = useCallback(() => setHeaderHovered(true), []);
	const handleHeaderMouseLeave = useCallback(() => setHeaderHovered(false), []);
	const {
		items,
		totalCount,
		summary,
		summaryDetails,
		generatedAt,
		isLoading,
		hasError,
		retry,
		hasDebugData,
		rawResponse,
	} = useInsightsFeed({
		assistanceServiceParams,
		debugInfo: debugEnabled,
	});

	useEffect(() => {
		if (!hasScreenFired.current) {
			hasScreenFired.current = true;
			fireAnalyticsEvent({
				eventType: 'screen',
				name: 'insightsFeedScreen',
			});
		}
	}, [fireAnalyticsEvent]);

	useEffect(() => {
		if (hasError) {
			fireAnalyticsEvent({
				eventType: 'screen',
				name: 'insightsFeedErrorScreen',
				tags: ['atlaskit'],
			});
		}
	}, [hasError, fireAnalyticsEvent]);

	useEffect(() => {
		if (!isLoading && !hasError && items.length > 0 && !hasContentFired.current) {
			hasContentFired.current = true;
			const contentAttributes = {
				insightTypes: items.map((item) => item.category),
				insightCount: totalCount,
				groupCount: items.length,
			};
			fireAnalyticsEvent({
				eventType: 'track',
				actionSubject: 'insightsFeedContent',
				action: 'displayed',
				attributes: contentAttributes,
			});
			onFeedContentDisplayed(contentAttributes);
		}
	}, [isLoading, hasError, items, totalCount, fireAnalyticsEvent, onFeedContentDisplayed]);

	const handleInsightClick = useCallback(
		(insightId: string) => {
			for (const group of items) {
				const subItem = group.subItems?.find((s) => s.id === insightId);
				if (subItem?.detailsAdf) {
					onInsightCardClicked(group.category);
					void onInsightReady?.(subItem.detailsAdf, {
						conversationTitle: subItem.title,
						conversationIcon: group.icon,
						conversationIconAppearance: group.iconAppearance,
						followUps: subItem.followUps ?? [],
					});
					return;
				}
			}
		},
		[items, onInsightReady, onInsightCardClicked],
	);

	const headerBlock = (
		<Box
			xcss={debugStyles.headerHoverContainer}
			onMouseEnter={handleHeaderMouseEnter}
			onMouseLeave={handleHeaderMouseLeave}
		>
			<InsightsFeedHeader
				totalCount={totalCount}
				summary={summary}
				summaryDetails={summaryDetails}
				generatedAt={generatedAt}
				isFullScreen={isFullScreen}
				connectAppsAction={connectAppsAction}
				animationEnabled={!isFullScreen}
			/>
			{isDebugGateEnabled && (
				<Box
					xcss={
						headerHovered || debugEnabled
							? debugStyles.debugToggleWrapperActive
							: debugStyles.debugToggleWrapper
					}
				>
					<DebugToggle isEnabled={debugEnabled} onChange={setDebugEnabled} />
				</Box>
			)}
		</Box>
	);
	const animatedHeaderBlock = isFullScreen ? (
		<AnimatedFeedSection index={0} isEnabled testId="insights-feed-header-animation-section">
			{headerBlock}
		</AnimatedFeedSection>
	) : (
		headerBlock
	);
	const groupAnimationStartIndex = isFullScreen ? 1 : 2;

	return (
		<Flex direction="column" xcss={styles.mainContent}>
			{chatRefreshHeader}
			{isLoading && <InsightsFeedLoadingShimmer />}
			{hasError && (
				<InsightsFeedError
					onRetry={() => {
						onRetryClicked();
						retry();
					}}
				/>
			)}
			{!isLoading && !hasError && (
				<div
					css={[
						scrollContainerBaseStyles,
						scrollContainerPaddingStyles[isFullScreen ? 'fullScreen' : 'default'],
					]}
					data-testid="insights-feed-scroll-container"
				>
					{isFullScreen && animatedHeaderBlock}
					<Box xcss={styles.centeredContainer}>
						{!isFullScreen && headerBlock}
						{hasDebugData && <DebugSummaryBar items={items} rawResponse={rawResponse} />}
						<Stack xcss={styles.listContainer}>
							{items.map((item, index) => (
								<AnimatedFeedSection
									key={item.id}
									index={groupAnimationStartIndex + index}
									isEnabled
									testId={`insights-feed-group-animation-section-${index}`}
								>
									<InsightsFeedGroup
										item={item}
										onInsightPress={handleInsightClick}
										isFullScreen={isFullScreen}
										onLinkClick={onUrlClicked}
										headerExtra={
											hasDebugData && item.debugInfo ? (
												<DebugGroupPanel debugInfo={item.debugInfo} />
											) : undefined
										}
										renderSubItemExtra={
											hasDebugData
												? (subItem) =>
														subItem.thinking ? (
															<DebugThinkingPanel thinking={subItem.thinking} />
														) : null
												: undefined
										}
									/>
								</AnimatedFeedSection>
							))}
						</Stack>
					</Box>
				</div>
			)}
			{!isLoading && !hasError && items.length > 0 && <InsightsFeedAiDisclaimer />}
		</Flex>
	);
};

export const InsightsFeed: React.MemoExoticComponent<(props: InsightsFeedProps) => JSX.Element> =
	memo(InsightsFeedBase);
